import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { useGetMeetingGuidanceQuery } from '../api/meetingGuidanceApi';
import type { MeetingGuidanceWidgetProps } from '../types';

const formatLabel = (value: string) =>
  value
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

export const MeetingGuidanceWidget = ({
  planResultRootId,
  planResultTypeId,
  onClose,
}: MeetingGuidanceWidgetProps) => {
  const { data, isLoading, isError, error, refetch } = useGetMeetingGuidanceQuery({
    planResultRootId,
    planResultTypeId,
  });

  if (isLoading) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography variant="h4SBold">Loading meeting guidance...</Typography>
      </Box>
    );
  }

  if (isError || !data) {
    const errorMessage = String(
      (error as { data?: { error?: string } } | undefined)?.data?.error ??
        'Failed to load meeting guidance.',
    );

    return (
      <Box sx={{ p: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Alert severity="error">{errorMessage}</Alert>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="contained" onClick={() => refetch()}>
            Retry
          </Button>
          {onClose && (
            <Button variant="outlined" onClick={onClose}>
              Close
            </Button>
          )}
        </Box>
      </Box>
    );
  }

  const {
    prep_brief: prepBrief,
    meeting_context: meetingContext,
    live_companion: liveCompanion,
  } = data;

  return (
    <Box sx={{ p: 4, display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 2,
        }}
      >
        <Stack spacing={1} sx={{ maxWidth: 840, minWidth: 0 }}>
          <Typography variant="h3SMedium" component="div">
            Meeting Guidance
          </Typography>
          <Typography
            variant="par02Regular"
            color="text.secondary"
            component="div"
          >
            {meetingContext.plan.plan_name}
          </Typography>
          <Typography variant="par02Regular" component="div">
            {prepBrief.overview}
          </Typography>
        </Stack>
        {onClose && (
          <Button variant="outlined" onClick={onClose}>
            Close
          </Button>
        )}
      </Box>

      <Card>
        <CardContent>
          <Typography variant="h5SMedium" sx={{ mb: 2 }}>
            Classification
          </Typography>
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            <Chip label={`Level: ${formatLabel(prepBrief.classification.client_level)}`} />
            <Chip label={`Life Stage: ${formatLabel(prepBrief.classification.life_stage)}`} />
            <Chip
              label={`Household: ${formatLabel(prepBrief.classification.household_type)}`}
            />
            <Chip
              label={`Review: ${formatLabel(prepBrief.classification.review_recency)}`}
            />
          </Stack>
        </CardContent>
      </Card>

      <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 3 }}>
        <Card>
          <CardContent>
            <Typography variant="h5SMedium" sx={{ mb: 2 }}>
              Agenda
            </Typography>
            <List disablePadding>
              {prepBrief.agenda.map((item, index) => (
                <Box key={item.item_id}>
                  <ListItem disableGutters>
                    <ListItemText
                      primary={`${index + 1}. ${item.title}`}
                      secondary={item.is_required ? 'Core agenda item' : 'Conditional agenda item'}
                      primaryTypographyProps={{
                        sx: { color: 'caravelOrangePrimary' },
                      }}
                    />
                  </ListItem>
                  {index < prepBrief.agenda.length - 1 && <Divider />}
                </Box>
              ))}
            </List>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Typography variant="h5SMedium" sx={{ mb: 2 }}>
              Plan Snapshot
            </Typography>
            <Stack spacing={1}>
              <Typography variant="par02Regular">
                Goals: {meetingContext.plan.goal_count}
              </Typography>
              <Typography variant="par02Regular">
                Accounts: {meetingContext.plan.account_count}
              </Typography>
              <Typography variant="par02Regular">
                Household: {meetingContext.household.is_family_plan ? 'Family' : 'Individual'}
              </Typography>
              <Typography variant="par02Regular">
                Tier: {meetingContext.household.client_tier ?? 'Unknown'}
              </Typography>
            </Stack>
          </CardContent>
        </Card>
      </Box>

      <Card>
        <CardContent>
          <Typography variant="h5SMedium" sx={{ mb: 2 }}>
            Activated Topics
          </Typography>
          <Stack spacing={2}>
            {prepBrief.activated_topics.map((topic) => (
              <Box
                key={topic.topic_id}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                }}
              >
                <Typography variant="par01SemiBold" component="div">
                  {topic.title}
                </Typography>
                <Typography
                  variant="par02Regular"
                  color="text.secondary"
                  component="div"
                >
                  Agenda Section: {formatLabel(topic.agenda_item_id)}
                </Typography>
                <Typography variant="par02Regular" component="div">
                  {topic.reason}
                </Typography>
              </Box>
            ))}
            {prepBrief.activated_topics.length === 0 && (
              <Typography variant="par02Regular">
                No dynamic topics were activated for this meeting.
              </Typography>
            )}
          </Stack>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h5SMedium" component="div" sx={{ mb: 1 }}>
            Live Meeting Companion
          </Typography>
          <Typography
            variant="par02Regular"
            color="text.secondary"
            component="div"
            sx={{ mb: 3 }}
          >
            {liveCompanion.overview}
          </Typography>
          <Stack spacing={3}>
            {liveCompanion.sections.map((section) => (
              <Box key={section.agenda_item_id}>
                <Typography variant="par01SemiBold" component="div" sx={{ mb: 1 }}>
                  {section.title}
                </Typography>
                <List disablePadding sx={{ listStyleType: 'disc', pl: 3 }}>
                  {section.prompts.map((prompt, index) => (
                    <ListItem
                      key={`${section.agenda_item_id}-prompt-${index}`}
                      disableGutters
                      sx={{ display: 'list-item', py: 0.25 }}
                    >
                      <ListItemText
                        primary={prompt}
                        primaryTypographyProps={{ variant: 'par02Regular' }}
                      />
                    </ListItem>
                  ))}
                </List>
                {section.watchouts.length > 0 && (
                  <Stack spacing={1} sx={{ mt: 1.5 }}>
                    <Typography variant="par02Regular" color="text.secondary">
                      Watch-outs
                    </Typography>
                    <List disablePadding sx={{ listStyleType: 'disc', pl: 3 }}>
                      {section.watchouts.map((watchout, index) => (
                        <ListItem
                          key={`${section.agenda_item_id}-watchout-${index}`}
                          disableGutters
                          sx={{ display: 'list-item', py: 0.25 }}
                        >
                          <ListItemText
                            primary={watchout}
                            primaryTypographyProps={{ variant: 'par02Regular' }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Stack>
                )}
              </Box>
            ))}
          </Stack>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h5SMedium" sx={{ mb: 2 }}>
            Recommendation Reminders
          </Typography>
          {liveCompanion.recommendation_reminders.length > 0 ? (
            <List disablePadding sx={{ listStyleType: 'disc', pl: 3 }}>
              {liveCompanion.recommendation_reminders.map((reminder, index) => (
                <ListItem
                  key={`${index}-${reminder}`}
                  disableGutters
                  sx={{ display: 'list-item', py: 0.5 }}
                >
                  <ListItemText
                    primary={reminder}
                    primaryTypographyProps={{ variant: 'par02Regular' }}
                  />
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography variant="par02Regular">
              No recommendation reminders were generated for this meeting.
            </Typography>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h5SMedium" sx={{ mb: 2 }}>
            Advisor Notes
          </Typography>
          <List disablePadding sx={{ listStyleType: 'disc', pl: 3 }}>
            {prepBrief.advisor_notes.map((note, index) => (
              <ListItem
                key={`${index}-${note}`}
                disableGutters
                sx={{ display: 'list-item', py: 0.5 }}
              >
                <ListItemText
                  primary={note}
                  primaryTypographyProps={{ variant: 'par02Regular' }}
                />
              </ListItem>
            ))}
          </List>
        </CardContent>
      </Card>
    </Box>
  );
};
